import { Button, Space } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Organisation } from 'store/organisation/models';
import { BoldNameSpan, GraySpan } from 'components/Custom/spans';
import { DeleteOutlined } from '@ant-design/icons';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { getProviderName } from 'utils/get-provider-name';
import { useCountryLabel } from 'hooks/useCountryLabel';
import XeroSmallIcon from 'assets/images/xero_small.png';
import DearSmallIcon from 'assets/images/dear_small.png';
import ZohoSmallIcon from 'assets/images/zoho_small.png';
import QuickbooksSmallIcon from 'assets/images/quickbooks_small.png';
import ExcelSmallIcon from 'assets/images/excel_small.png';

// Provider icon mapping
const providerIcons: Record<string, string> = {
  xero: XeroSmallIcon,
  dear: DearSmallIcon,
  zoho: ZohoSmallIcon,
  quickbooks: QuickbooksSmallIcon,
  excel: ExcelSmallIcon,
};

interface RenderOrganizationDetailProps {
  record: Organisation;
  showOption?: boolean;
  showRole?: boolean;
  hideCurrency?: boolean;
  showDelete?: boolean;
  noPadding?: boolean;
  organisationRemove?: (organisationId: string) => void;
}

export const RenderOrganizationDetail: React.FC<RenderOrganizationDetailProps> = ({
  record,
  showOption = false,
  showRole = false,
  hideCurrency = false,
  showDelete = false,
  noPadding = false,
  organisationRemove,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const onOrganisationRemove = () =>
    organisationRemove && organisationRemove(record.uuid);

  const getCountryName = () => {
    if (!record?.country) return null;
    return localizeCountry(record.country);
  };

  const getProviderLogo = () => {
    if (!record?.providerName) return null;
    const iconSrc = providerIcons[record.providerName.toLowerCase()];
    if (!iconSrc) return null;
    return (
      <img
        src={iconSrc}
        width={32}
        height={32}
        alt={record.providerName}
        style={{ borderRadius: '50%' }}
      />
    );
  };

  return (
    <Space
      style={{
        borderBottom: !showOption ? '1px solid var(--color-border, #dbeae3)' : 'none',
        padding: noPadding ? '0px' : !showOption ? '14px 0px' : '8px 0px',
      }}
    >
      {getProviderLogo()}
      <Space
        direction="vertical"
        style={{ rowGap: '0', minWidth: !showOption ? 210 : 170 }}
      >
        <BoldNameSpan
          style={{ fontSize: !showOption ? 15 : 13, fontWeight: 600 }}
        >
          {record?.friendlyName}
        </BoldNameSpan>

        {!hideCurrency && record?.currency && (
          <BoldNameSpan //@ts-ignore
            fontWeight={400}
            style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
          >
            {(() => {
              const countryName = getCountryName();
              if (countryName && record?.currency) {
                return `${countryName} - ${record.currency}`;
              }
              return record.currency;
            })()}
          </BoldNameSpan>
        )}
      </Space>
      {showRole && record?.providerName && (
        <GraySpan style={{ marginLeft: '0' }}>
          {getProviderName(record.providerName)}
        </GraySpan>
      )}

      {showDelete && (
        <StyledPopconfirm // @ts-ignore
          title={t('organisation.remove-organisation-title')}
          description={
            <span>{t('organisation.remove-organisation-description')}</span>
          }
          onConfirm={onOrganisationRemove}
          placement="left"
          okText={t('common.yes')}
          cancelText={t('common.cancel')}
        >
          <Button
            type="link"
            shape="circle"
            icon={<DeleteOutlined />}
            aria-label={t('common.delete')}
          />
        </StyledPopconfirm>
      )}
    </Space>
  );
};

